import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  arrayUnion,
  updateDoc,
  where,
  type DocumentData,
  type Firestore,
  type Query,
} from 'firebase/firestore'
import {
  EMPTY_STATS,
  NORMALIZED_TEXT_VERSION,
  asinOf,
  baseUnitOf,
  findMerchant,
  parseOnde,
  gtinOf,
  normalizedText,
  pricePerBaseUnit,
} from '@domain/product'
import type { Auth } from 'firebase/auth'
import type {
  BaseUnit,
  Merchant,
  RecordedBy,
  RecordedByKind,
  PricePoint,
  PriceSource,
  Product,
  ProductFamily,
  Unit,
} from '@domain/product'
import type { NewProduct, ProductRepository } from '@domain/ports/finance-repository'
import { UNITS } from '@domain/product'
import {
  booleanOr,
  optionalCents,
  optionalText,
  requireCents,
  requireCivilDate,
  requireInteger,
  requireIso,
  requireOneOf,
  requireText,
  oneOfOrNull,
  subDocument,
  textOr,
  toIso,
} from './converters'
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
  type FirebaseStorage,
} from 'firebase/storage'
import { HOUSEHOLD_ID } from './household-repository'

/**
 * O catálogo de produtos e os pontos de preço.
 *
 * ## O nome normalizado é escrito AQUI, junto da versão
 *
 * A tela manda o nome; este adapter grava `normalizedName` e
 * `normalizedVersion`. Deixá-los à tela permitiria gravar um normalizado que não
 * corresponde ao nome, e o matching da Fase 8 casaria pelo campo errado sem que
 * nada reclamasse.
 *
 * A **versão** vai junto porque `normalizedText` vai mudar — abreviação de
 * mercado é poço sem fundo — e é ela que permite reprocessar só o que precisa:
 * `where('normalizedVersion', '<', NORMALIZED_TEXT_VERSION)`.
 *
 * ## O preço por unidade base é calculado aqui, com a função do domínio
 *
 * `pricePerBaseUnit`, a mesma que os testes do domínio exercitam — e não uma
 * conta escrita à mão. Ela é o que faz 500 ml a R$ 3,99 comparar com 1 L a
 * R$ 6,99, e uma segunda implementação seria a terceira chance de errar o
 * mesmo número.
 *
 * **Produto sem tamanho declarado não gera ponto normalizado**, e a porta recusa
 * em vez de inventar: sem `size` e `unit` não existe unidade base, e gravar zero
 * faria o produto virar "o mais barato" de toda comparação.
 *
 * ## O ponto é IMUTÁVEL, e a regra cobra
 *
 * `allow update, delete: if false` em `priceHistory`. Corrigir é criar outro com
 * `supersedesId` — nunca se reescreve o passado. Por isso esta porta não tem
 * `updatePrice` nem `removePrice`.
 */

const BASE_UNITS = ['ml', 'g', 'cm', 'un'] as const
const SOURCES = ['purchase', 'manual', 'nota_parana', 'web'] as const
const ORIGINS = ['barcode', 'exact', 'learned', 'ai', 'manual'] as const

/**
 * **As listas acima são de runtime**, e o `tsc` não as liga sozinhas aos tipos.
 * Mesma armadilha de `CASH_IMPACTS`: uma unidade que exista no tipo e falte aqui
 * faz **todo produto com ela lançar na leitura**, e o catálogo some por causa de
 * um documento.
 */
type Equals<A, B> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false
const _unitsCobremAUniao: Equals<Unit, (typeof UNITS)[number]> = true
const _basesCobremAUniao: Equals<BaseUnit, (typeof BASE_UNITS)[number]> = true
const _sourcesCobremAUniao: Equals<PriceSource, (typeof SOURCES)[number]> = true
void _unitsCobremAUniao
void _basesCobremAUniao
void _sourcesCobremAUniao

function productsPath(): string {
  return `households/${HOUSEHOLD_ID}/products`
}
function pricesPath(): string {
  return `households/${HOUSEHOLD_ID}/priceHistory`
}
function matchesPath(): string {
  return `households/${HOUSEHOLD_ID}/productMatches`
}

function toStats(value: unknown, where: string): Product['stats'] {
  if (value === undefined || value === null) return EMPTY_STATS
  const stats = subDocument(value)
  return {
    purchaseCount: requireInteger(stats['purchaseCount'] ?? 0, `${where}.purchaseCount`),
    lastPriceCents: optionalCents(stats['lastPriceCents'], `${where}.lastPriceCents`),
    lastPurchasedAt: optionalText(stats['lastPurchasedAt']),
    avgPerBaseUnitMilliCents: optionalCents(
      stats['avgPerBaseUnitMilliCents'],
      `${where}.avgPerBaseUnitMilliCents`,
    ),
    minPerBaseUnitMilliCents: optionalCents(
      stats['minPerBaseUnitMilliCents'],
      `${where}.minPerBaseUnitMilliCents`,
    ),
    minPriceMerchantId: optionalText(stats['minPriceMerchantId']),
    baseUnit:
      stats['baseUnit'] === undefined || stats['baseUnit'] === null
        ? null
        : requireOneOf(stats['baseUnit'], BASE_UNITS, `${where}.baseUnit`),
  }
}

export function toProduct(id: string, data: DocumentData): Product {
  const where = `products/${id}`
  const unit = data['unit']
  return {
    id,
    householdId: HOUSEHOLD_ID,
    // Nome cai num default: produto sem nome é feio na lista, não errado, e
    // derrubar o catálogo por causa de um documento seria pior.
    name: textOr(data['name'], ''),
    normalizedName: textOr(data['normalizedName'], ''),
    normalizedVersion: requireInteger(data['normalizedVersion'] ?? 0, `${where}.normalizedVersion`),
    brand: optionalText(data['brand']),
    categoryId: optionalText(data['categoryId']),
    familyId: optionalText(data['familyId']),
    // Tamanho LANÇA quando corrompido: ele divide o preço, e um valor estranho
    // produziria uma comparação errada em silêncio.
    size:
      data['size'] === undefined || data['size'] === null
        ? null
        : requireCents(data['size'], `${where}.size`),
    unit: unit === undefined || unit === null ? null : requireOneOf(unit, UNITS, `${where}.unit`),
    packQuantity: requireInteger(data['packQuantity'] ?? 1, `${where}.packQuantity`),
    barcode: optionalText(data['barcode']),
    gtin: optionalText(data['gtin']),
    asin: optionalText(data['asin']),
    imageUrl: optionalText(data['imageUrl']),
    stats: toStats(data['stats'], `${where}.stats`),
    isMonitored: booleanOr(data['isMonitored'], false),
    archivedAt: toIso(data['archivedAt']),
    createdAt: requireIso(data['createdAt'], `${where}.createdAt`),
    updatedAt: requireIso(data['updatedAt'], `${where}.updatedAt`),
  }
}

export function toPricePoint(id: string, data: DocumentData): PricePoint {
  const where = `priceHistory/${id}`
  return {
    id,
    householdId: HOUSEHOLD_ID,
    productId: requireText(data['productId'], `${where}.productId`),
    priceCents: requireCents(data['priceCents'], `${where}.priceCents`),
    quantity: requireCents(data['quantity'], `${where}.quantity`),
    totalCents: requireCents(data['totalCents'], `${where}.totalCents`),
    pricePerBaseUnitMilliCents: requireCents(
      data['pricePerBaseUnitMilliCents'],
      `${where}.pricePerBaseUnitMilliCents`,
    ),
    baseUnit: requireOneOf(data['baseUnit'], BASE_UNITS, `${where}.baseUnit`),
    merchantId: optionalText(data['merchantId']),
    sourceUrl: optionalText(data['sourceUrl']),
    recordedBy: quemRegistrou(data['recordedBy']),
    note: optionalText(data['note']),
    observedAt: requireCivilDate(data['observedAt'], `${where}.observedAt`),
    source: requireOneOf(data['source'], SOURCES, `${where}.source`),
    receiptItemId: optionalText(data['receiptItemId']),
    supersedesId: optionalText(data['supersedesId']),
    // Ponto registrado antes da decisão 199 não tem os dois. `null` é a
    // resposta certa — "ninguém anulou" —, e exigi-los faria todo histórico
    // antigo derrubar a leitura do produto.
    voidedAt: toIso(data['voidedAt']),
    voidedBy: quemAnulou(data['voidedBy']),
    createdAt: requireIso(data['createdAt'], `${where}.createdAt`),
  }
}

function byProductQuery(db: Firestore, productId: string): Query {
  return query(collection(db, pricesPath()), where('productId', '==', productId))
}

function aliasesQuery(db: Firestore, productId: string): Query {
  return query(collection(db, matchesPath()), where('productId', '==', productId))
}

/** O registro que `firestore-indexes.test.ts` varre. Ver `INVOICE_QUERIES`. */
export const PRODUCT_QUERIES: Record<string, (db: Firestore, id: string) => Query> = {
  priceHistory: byProductQuery,
  productMatches: aliasesQuery,
}

/** Sem tamanho declarado não há unidade base, e sem ela não há comparação. */
export class SemTamanhoError extends Error {}

function campos(input: NewProduct): DocumentData {
  return {
    name: input.name,
    // Normalizado aqui, com a versão junto. Ver o cabeçalho.
    normalizedName: normalizedText(input.name),
    normalizedVersion: NORMALIZED_TEXT_VERSION,
    brand: input.brand,
    categoryId: input.categoryId,
    size: input.size,
    unit: input.unit,
    packQuantity: input.packQuantity,
    barcode: input.barcode,
    // Normalizados AQUI, como o nome. Deixá-los à tela permitiria gravar um
    // GTIN que não corresponde ao código digitado — e a cascata casaria pelo
    // campo errado sem nada reclamar. Ver `gtin.ts`.
    gtin: gtinOf(input.barcode),
    asin: asinOf(input.asin),
    isMonitored: input.isMonitored,
    familyId: input.familyId,
  }
}

function merchantsPath(): string {
  return `households/${HOUSEHOLD_ID}/merchants`
}

function lojaDe(id: string, data: DocumentData): Merchant {
  const onde = `merchants/${id}`
  return {
    id,
    householdId: textOr(data['householdId'], HOUSEHOLD_ID),
    name: requireText(data['name'], `${onde}.name`),
    normalizedName: textOr(data['normalizedName'], ''),
    // Loja criada antes da decisão 195 não tem os dois campos abaixo. Vazio e
    // `null` são as respostas certas — "nenhum padrão conhecido" e "nenhuma
    // categoria sugerida" —, e exigi-los faria toda loja antiga derrubar a
    // leitura do catálogo inteiro.
    patterns: Array.isArray(data['patterns'])
      ? (data['patterns'] as unknown[]).filter((x): x is string => typeof x === 'string')
      : [],
    defaultCategoryId: optionalText(data['defaultCategoryId']),
    createdAt: requireIso(data['createdAt'], `${onde}.createdAt`),
    updatedAt: requireIso(data['updatedAt'], `${onde}.updatedAt`),
  }
}

function familiesPath(): string {
  return `households/${HOUSEHOLD_ID}/productFamilies`
}

function familiaDe(id: string, data: DocumentData): ProductFamily {
  const onde = `productFamilies/${id}`
  return {
    id,
    householdId: textOr(data['householdId'], HOUSEHOLD_ID),
    name: requireText(data['name'], `${onde}.name`),
    normalizedName: textOr(data['normalizedName'], ''),
    archivedAt: toIso(data['archivedAt']),
    createdAt: requireIso(data['createdAt'], `${onde}.createdAt`),
    updatedAt: requireIso(data['updatedAt'], `${onde}.updatedAt`),
  }
}

function imagePath(productId: string): string {
  return `households/${HOUSEHOLD_ID}/products/${productId}`
}

/** O array de runtime que cobre a união. Ver `comment-repository.ts`. */
const RECORDED_BY_KINDS = ['user', 'ai', 'other'] as const
const _kindsCobremAUniao: Equals<RecordedByKind, (typeof RECORDED_BY_KINDS)[number]> = true
void _kindsCobremAUniao

/**
 * Quem registrou, lido com desconfiança.
 *
 * **Ponto antigo não tem o campo**, e é o caso comum: todo preço gravado antes
 * da decisão 192. Ele vira `other`/"alguém" em vez de derrubar a leitura — um
 * histórico que some porque um campo novo não existe no passado seria o pior
 * dos dois mundos.
 */
function quemRegistrou(value: unknown): RecordedBy {
  const dados = value === null || value === undefined ? {} : subDocument(value)
  const kind = oneOfOrNull(dados['kind'], RECORDED_BY_KINDS) ?? 'other'
  return {
    kind,
    id: optionalText(dados['id']),
    label: textOr(dados['label'], kind === 'ai' ? 'um agente' : 'alguém'),
  }
}

/**
 * Quem anulou, ou `null`.
 *
 * Diferente de `quemRegistrou`, que inventa um "alguém" para o campo ausente:
 * aqui a ausência **significa** algo — ninguém anulou —, e transformá-la num
 * `other`/"alguém" faria todo ponto do histórico parecer anulado por um
 * desconhecido.
 */
function quemAnulou(value: unknown): RecordedBy | null {
  if (value === null || value === undefined) return null
  return quemRegistrou(value)
}

/**
 * Uma lista lida documento a documento, **sem deixar um podre derrubar o
 * resto**.
 *
 * ## O dia em que um ponto de preço apagou o catálogo
 *
 * 2026-09-28: dois documentos de `priceHistory` nasceram com `observedAt: ''`,
 * porque a nota que os gerou não tinha data e a Function passou `''` no lugar
 * dela. Na volta, `requireCivilDate` recusou — corretamente. Só que a tela de
 * Produtos lê o histórico de **todos** os produtos de uma vez, e um `throw`
 * dentro do `map` derrubava o `Promise.all` inteiro: 231 pontos bons, 98
 * produtos e 22 grupos viraram *"Não foi possível carregar os grupos"*.
 *
 * ## O conversor continua rígido; quem mudou foi a lista
 *
 * A exigência por documento não afrouxou — campo corrompido continua lançando,
 * porque devolver um número inventado é pior. O que mudou é o alcance do
 * estrago: o documento ilegível sai da lista e vai para o console, nomeado, e
 * os outros chegam à tela.
 *
 * **O custo é honesto e é este:** um ponto de preço que não converte some da
 * comparação sem nada na tela dizendo. É menos ruim que a tela inteira sumir —
 * mas é por isso que o erro vai para o console com o caminho do documento, e
 * não em silêncio.
 */
function lidos<T>(
  docs: { id: string; data: () => DocumentData }[],
  converte: (id: string, data: DocumentData) => T,
  onde: string,
): T[] {
  const bons: T[] = []
  for (const d of docs) {
    try {
      bons.push(converte(d.id, d.data()))
    } catch (erro: unknown) {
      console.error(`${onde}/${d.id} não pôde ser lido e ficou de fora da lista`, erro)
    }
  }
  return bons
}

export function createFirebaseProductRepository(
  db: Firestore,
  storage: FirebaseStorage,
  auth: Auth,
): ProductRepository {
  return {
    setImage: async (productId, photo) => {
      // Caminho fixo pelo id do produto, e **não** um id aleatório: assim
      // trocar a foto sobrescreve a anterior em vez de deixar lixo acumulando
      // no Storage. É o inverso da nota, onde a imagem antiga é prova e não
      // pode ser sobrescrita.
      const destino = storageRef(storage, imagePath(productId))
      await uploadBytes(destino, photo, { contentType: photo.type || 'image/jpeg' })
      const url = await getDownloadURL(destino)
      await updateDoc(doc(db, productsPath(), productId), {
        imageUrl: url,
        updatedAt: serverTimestamp(),
      })
      return url
    },

    clearImage: async (productId) => {
      await updateDoc(doc(db, productsPath(), productId), {
        imageUrl: null,
        updatedAt: serverTimestamp(),
      })
      // O documento primeiro: se o delete do arquivo falhar, sobra um arquivo
      // que ninguém referencia — barato. Na ordem inversa, uma falha deixaria o
      // produto apontando para uma imagem que não existe mais, e a tela
      // mostraria um retângulo quebrado.
      await deleteObject(storageRef(storage, imagePath(productId))).catch((erro: unknown) => {
        console.error('clearImage: a imagem não foi apagada do Storage', erro)
      })
    },

    listAliases: async (productId) => {
      const snap = await getDocs(aliasesQuery(db, productId))
      return snap.docs.map((d) => {
        const data = d.data()
        return {
          id: d.id,
          productId,
          normalizedText: textOr(data['normalizedText'], ''),
          // Match aprendido antes de 2026-09-03 não tem estes campos, e a
          // ausência lê como `null` — que é o estado correto: ele vale pelo
          // texto, e nunca soube de loja nenhuma.
          origin: requireOneOf(data['origin'] ?? 'learned', ORIGINS, `${d.id}.origin`),
          merchantCnpj: optionalText(data['merchantCnpj']),
          merchantName: optionalText(data['merchantName']),
          rawCode: optionalText(data['rawCode']),
        }
      })
    },

    removeAlias: async (id) => {
      await deleteDoc(doc(db, matchesPath(), id))
    },

    listMerchants: async () => {
      const snap = await getDocs(collection(db, merchantsPath()))
      return snap.docs
        .map((d) => lojaDe(d.id, d.data()))
        .sort((a, b) => a.normalizedName.localeCompare(b.normalizedName))
    },

    learnMerchantCategory: async ({ key, label, categoryId }) => {
      // Chave vazia não ensina nada e criaria uma loja sem padrão que casaria
      // com tudo depois.
      if (key === '') return

      const snap = await getDocs(collection(db, merchantsPath()))
      const lojas = snap.docs.map((d) => lojaDe(d.id, d.data()))

      // Pelo padrão primeiro: é o que o extrato diz, e é exato. Só depois pelo
      // nome — assim "Uber" cadastrado à mão num registro de preço e `uber`
      // vindo do extrato viram a MESMA loja, em vez de duas com o mesmo nome.
      const porPadrao = lojas.find((m) => m.patterns.includes(key))
      const alvo = porPadrao ?? findMerchant(label, lojas)

      if (alvo === null || alvo === undefined) {
        await addDoc(collection(db, merchantsPath()), {
          householdId: HOUSEHOLD_ID,
          name: label,
          normalizedName: normalizedText(label),
          patterns: [key],
          defaultCategoryId: categoryId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
        return
      }

      await updateDoc(doc(db, merchantsPath(), alvo.id), {
        // `arrayUnion` e não um array remontado: dois aparelhos classificando
        // grupos diferentes da mesma loja ao mesmo tempo perderiam um padrão se
        // cada um escrevesse a lista que leu.
        patterns: arrayUnion(key),
        // A decisão nova ganha da antiga, sem perguntar. Quem reclassifica um
        // grupo está corrigindo — e uma sugestão velha que resistisse à
        // correção seria pior do que nenhuma.
        defaultCategoryId: categoryId,
        updatedAt: serverTimestamp(),
      })
    },

    listFamilies: async () => {
      // Sem `orderBy`: são poucas, e ordenar em memória evita um índice para
      // uma tela que já lê todas.
      const snap = await getDocs(collection(db, familiesPath()))
      return lidos(snap.docs, familiaDe, 'productFamilies').sort((a, b) =>
        a.normalizedName.localeCompare(b.normalizedName),
      )
    },

    createFamily: async (name) => {
      const criada = await addDoc(collection(db, familiesPath()), {
        householdId: HOUSEHOLD_ID,
        name,
        // Normalizado aqui, como o nome do produto e pelo mesmo motivo: deixá-lo
        // à tela permitiria gravar um normalizado que não corresponde ao nome.
        normalizedName: normalizedText(name),
        archivedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return criada.id
    },

    archive: async (id) => {
      await updateDoc(doc(db, productsPath(), id), {
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    },

    remove: async (id) => {
      // Sem cascata: quem chama já sabe que não há histórico — ver a porta. A
      // foto no Storage fica, e é o preço de não fazer uma segunda escrita
      // para um caso que só existe em produto recém-criado.
      await deleteDoc(doc(db, productsPath(), id))
    },

    renameFamily: async (id, name) => {
      // `normalizedName` junto, e derivado aqui: o par nome/normalizado é o que
      // faz a busca do seletor achar "Café" digitando "cafe", e deixá-los
      // divergir quebraria isso em silêncio — a família continuaria na lista,
      // só não seria mais encontrável pelo nome novo.
      await updateDoc(doc(db, familiesPath(), id), {
        name,
        normalizedName: normalizedText(name),
        updatedAt: serverTimestamp(),
      })
    },

    list: async () => {
      const snap = await getDocs(collection(db, productsPath()))
      return lidos(snap.docs, toProduct, 'products').sort((a, b) =>
        a.normalizedName.localeCompare(b.normalizedName),
      )
    },

    byId: async (id) => {
      const snap = await getDoc(doc(db, productsPath(), id))
      return snap.exists() ? toProduct(snap.id, snap.data()) : null
    },

    create: async (input) => {
      const created = await addDoc(collection(db, productsPath()), {
        ...campos(input),
        imageUrl: null,
        archivedAt: null,
        // **`stats` NÃO vai daqui** — a regra nega, e ele é derivado. O
        // conversor devolve `EMPTY_STATS` para o bloco ausente, então o produto
        // novo já lê certo antes de a Function rodar.
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return created.id
    },

    update: async (id, patch) => {
      const update: DocumentData = { updatedAt: serverTimestamp() }
      for (const [chave, valor] of Object.entries(patch)) {
        if (valor === undefined) continue
        update[chave] = valor
      }
      // O nome normalizado acompanha o nome, sempre. Sem isto, renomear um
      // produto o deixaria encontrável pelo nome velho e invisível pelo novo.
      if (patch.name !== undefined) {
        update['normalizedName'] = normalizedText(patch.name)
        update['normalizedVersion'] = NORMALIZED_TEXT_VERSION
      }
      await updateDoc(doc(db, productsPath(), id), update)
    },

    priceHistory: async (productId) => {
      const snap = await getDocs(byProductQuery(db, productId))
      return lidos(snap.docs, toPricePoint, 'priceHistory').sort((a, b) =>
        a.observedAt.localeCompare(b.observedAt),
      )
    },

    recordPrice: async (input) => {
      const produto = await getDoc(doc(db, productsPath(), input.productId))
      if (!produto.exists()) throw new Error(`produto ${input.productId} não existe`)
      const lido = toProduct(produto.id, produto.data())
      if (lido.size === null || lido.unit === null) {
        throw new SemTamanhoError(
          `o produto "${lido.name}" não tem tamanho declarado, então não há unidade base para comparar`,
        )
      }

      // **Onde** é resolvido aqui, e não pela tela: `parseOnde` decide se o que
      // a pessoa escreveu é lugar ou link, e a loja é achada-ou-criada pelo
      // nome normalizado. É o que faz a mesma loja digitada com grafias
      // diferentes continuar sendo uma.
      const { merchantName, sourceUrl } = parseOnde(input.where ?? '')
      let merchantId: string | null = null
      if (merchantName !== null) {
        const existentes = await getDocs(collection(db, merchantsPath()))
        const achada = findMerchant(
          merchantName,
          existentes.docs.map((d) => lojaDe(d.id, d.data())),
        )
        if (achada !== null) merchantId = achada.id
        else {
          const nova = await addDoc(collection(db, merchantsPath()), {
            householdId: HOUSEHOLD_ID,
            name: merchantName,
            normalizedName: normalizedText(merchantName),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          })
          merchantId = nova.id
        }
      }

      const created = await addDoc(collection(db, pricesPath()), {
        productId: input.productId,
        // O que se paga por uma embalagem, e o total pela quantidade comprada.
        priceCents: Math.round(input.totalCents / input.quantity),
        quantity: input.quantity,
        totalCents: input.totalCents,
        // Pela função do domínio, sobre UMA embalagem — o preço por unidade
        // base não depende de quantas foram compradas.
        pricePerBaseUnitMilliCents: pricePerBaseUnit(
          Math.round(input.totalCents / input.quantity),
          lido.size,
          lido.unit,
          lido.packQuantity,
        ),
        baseUnit: baseUnitOf(lido.unit),
        merchantId,
        sourceUrl,
        observedAt: input.observedAt,
        // A única fonte que a regra aceita do cliente. Ver `NewPricePoint`.
        source: 'manual',
        // **O `id` sai daqui, e não da tela.** Ver `NewPricePoint`.
        recordedBy: {
          kind: input.recordedBy.kind,
          id: input.recordedBy.kind === 'user' ? (auth.currentUser?.uid ?? null) : null,
          label: input.recordedBy.label,
        },
        note: input.note,
        receiptItemId: null,
        supersedesId: input.supersedesId,
        // `serverTimestamp()` como todo carimbo do cliente, e não o relógio do
        // aparelho: dois celulares registrando preço precisam de uma ordem, e
        // a do cliente pode estar errada. O relógio do host é proibido em
        // `src/` (decisão 79), e o `eslint` cobra.
        createdAt: serverTimestamp(),
      })
      return created.id
    },

    voidPricePoint: async (id) => {
      await updateDoc(doc(db, pricesPath(), id), {
        voidedAt: serverTimestamp(),
        // Sempre `user`: anular é julgamento, e a regra exige que o `id` seja
        // o de quem está pedindo. Um `ai`/`other` aqui permitiria anular sem
        // que ninguém respondesse pela decisão.
        voidedBy: {
          kind: 'user',
          id: auth.currentUser?.uid ?? null,
          label: auth.currentUser?.displayName ?? 'alguém',
        },
      })
    },
  }
}
